import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play as PlayIcon } from 'lucide-react';

import { Button, CoordioDropdown, FormField, ShellHeader, useStaticDropdownSource } from '@coordio/ui';
import { LoadingSkeleton, PageContainer } from '@coordio/ui/layout';

import { AGENT_DROPDOWN, agentToDropdownOption } from '../components/agents/agentDropdown';
import { agentService } from '../services/agentService';
import type { AgentDefinitionDto } from '../types/agent';

export const OrchestratorPage = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    agentService
      .getAgents({ pageSize: 200, isActive: true })
      .then((res) => setAgents(res.items))
      .finally(() => setLoading(false));
  }, []);

  const agentOptions = useMemo(() => agents.map(agentToDropdownOption), [agents]);
  const agentSource = useStaticDropdownSource(agentOptions);

  const handleLaunch = () => {
    if (selectedId) {
      navigate(`/orchestrator/${selectedId}`);
    }
  };

  return (
    <div className="orchestrator" data-testid="orchestrator-detail-page">
      <ShellHeader
        title="Multi-Agent Orchestrator"
        description="Pick an orchestrator agent and attach sub-agents. The orchestrator will delegate tasks to sub-agents via tool calls, with live progress in a Cursor-style timeline."
      />

      <PageContainer variant="narrow">
        <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
          <FormField label="Select Orchestrator Agent" className="mb-3">
            {loading ? (
              <LoadingSkeleton rows={1} data-testid="orchestrator-agent-select-loading" />
            ) : (
              <CoordioDropdown
                source={agentSource}
                value={selectedId}
                onChange={setSelectedId}
                {...AGENT_DROPDOWN}
                data-testid="orchestrator-agent-select"
                data-pw="orchestratorAgentId"
                data-field-type="select"
              />
            )}
          </FormField>

          <Button
            type="button"
            variant="default"
            size="lg"
            className="w-full gap-2"
            onClick={handleLaunch}
            disabled={!selectedId}
            data-testid="launch-orchestrator-btn"
          >
            <PlayIcon size={16} />
            Launch Orchestrator
          </Button>
        </div>
      </PageContainer>
    </div>
  );
};
